/**
 * SavedRoutesPanel — UI for loading saved trip plans
 *
 * Opens as a slide-up panel from the menu (mirrors FavoritesPanel.js).
 * Shows the list of routes saved from the desktop plan form; tapping
 * "Load" restores the inputs into the plan form and recalculates --
 * this is the mobile half of "plan on desktop, open on mobile".
 * No add-form here -- routes are created via the "Save This Route"
 * button in the desktop route-summary panel (window.saveCurrentRouteAs).
 */

import { savedRoutesService } from '../services/SavedRoutesService.js';
import { canPurchaseInApp } from '../services/purchase.js';

const PANEL_STYLES = `
.saved-routes-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 9999;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}
.saved-routes-panel {
    width: 100%;
    max-width: 480px;
    max-height: 80vh;
    background: white;
    border-radius: 16px 16px 0 0;
    overflow-y: auto;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom, 0px)) 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.saved-routes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}
.saved-routes-header h3 {
    margin: 0;
    font-size: 18px;
    color: #2c3e50;
}
.saved-routes-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #7f8c8d;
    padding: 4px 8px;
}
.saved-routes-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.saved-routes-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    gap: 12px;
}
.saved-routes-item:last-child {
    border-bottom: none;
}
.saved-routes-item-icon {
    font-size: 20px;
    width: 32px;
    text-align: center;
}
.saved-routes-item-info {
    flex: 1;
    min-width: 0;
}
.saved-routes-item-name {
    font-weight: 600;
    font-size: 14px;
    color: #2c3e50;
}
.saved-routes-item-path {
    font-size: 12px;
    color: #7f8c8d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.saved-routes-item-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}
.saved-routes-load-btn {
    background: #e3f2fd;
    border: none;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 11px;
    color: #1e88e5;
    cursor: pointer;
    font-weight: 600;
}
.saved-routes-load-btn:hover {
    background: #bbdefb;
}
.saved-routes-item-btn {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
    opacity: 0.6;
}
.saved-routes-item-btn:hover {
    opacity: 1;
}
.saved-routes-empty {
    text-align: center;
    padding: 24px;
    color: #7f8c8d;
    font-size: 14px;
}
`;

export async function openSavedRoutesPanel() {
    if (!window.isSubscribed) {
        const dropdown = document.getElementById('mobileDropdown');
        if (dropdown) dropdown.style.display = 'none';
        if (!window.authService.isSignedIn()) { window.authService.signIn(); return; }
        if (!canPurchaseInApp()) { window.showToast?.('Saved Routes is a Pro feature.'); return; }
        window.subscriptionService.startCheckout(window.authService.getUser()?.sub, window.authService.getUser()?.email);
        return;
    }

    injectStyles();

    const overlay = document.createElement('div');
    overlay.className = 'saved-routes-overlay';
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

    const panel = document.createElement('div');
    panel.className = 'saved-routes-panel';
    panel.onclick = (e) => e.stopPropagation();

    // Guards an in-flight open-time refresh against resolving AFTER a local
    // mutation (delete) and clobbering it with older data. Each refresh
    // captures requestId; invalidate() bumps it (called by both a fresh
    // refresh and any local mutation) so a superseded refresh renders
    // nothing when it finally resolves.
    let requestId = 0;
    const invalidate = () => ++requestId;

    const refresh = async () => {
        const thisRequest = invalidate();
        await savedRoutesService.load();
        if (thisRequest !== requestId) return; // superseded since this started
        renderPanel(panel, overlay, refresh, invalidate);
    };

    // Render immediately with whatever's cached (no blank-panel delay), then
    // refresh from the server -- savedRoutesService.load() otherwise only
    // ever runs once at app cold-start, so a route saved from desktop while
    // this mobile session was already running would never show up without
    // force-closing the app first. This is the actual point of the panel
    // (catching something saved elsewhere since this session started), so
    // it has to refetch on every open, not just render the stale cache.
    renderPanel(panel, overlay, refresh, invalidate);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Close the mobile dropdown menu
    const dropdown = document.getElementById('mobileDropdown');
    if (dropdown) dropdown.style.display = 'none';

    refresh();
}

function renderPanel(panel, overlay, refresh, invalidate) {
    const routes = savedRoutesService.getAll();

    let listHtml = '';
    if (routes.length === 0) {
        listHtml = '<div class="saved-routes-empty">No saved routes yet. Plan a trip on desktop and tap "Save This Route" to send it here.</div>';
    } else {
        listHtml = '<ul class="saved-routes-list">' + routes.map(r => `
            <li class="saved-routes-item" data-id="${r.id}">
                <span class="saved-routes-item-icon">🧭</span>
                <div class="saved-routes-item-info">
                    <div class="saved-routes-item-name">${r.name}</div>
                    <div class="saved-routes-item-path">${r.origin} → ${r.destination}</div>
                </div>
                <div class="saved-routes-item-actions">
                    <button class="saved-routes-load-btn" data-action="load" data-id="${r.id}">Load</button>
                    <button class="saved-routes-item-btn" data-action="delete" data-id="${r.id}">🗑️</button>
                </div>
            </li>
        `).join('') + '</ul>';
    }

    panel.innerHTML = `
        <div class="saved-routes-header">
            <h3>🧭 Saved Routes</h3>
            <button class="saved-routes-close" onclick="this.closest('.saved-routes-overlay').remove()">×</button>
        </div>
        ${listHtml}
    `;

    // Wire "Load" buttons
    panel.querySelectorAll('[data-action="load"]').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            if (window.loadSavedRoute) window.loadSavedRoute(id);
            overlay.remove();
        });
    });

    // Wire delete buttons
    panel.querySelectorAll('[data-action="delete"]').forEach(btn => {
        btn.addEventListener('click', async () => {
            const id = btn.dataset.id;
            btn.disabled = true;
            const success = await savedRoutesService.delete(id);
            if (!success) {
                alert('Failed to delete saved route. Please check your connection and try again.');
                btn.disabled = false;
                return;
            }
            // Deliberately NOT re-fetching from the server here. delete()
            // already filtered the local list correctly -- re-fetching
            // immediately after a write risks DynamoDB's default eventually
            // consistent read still returning the pre-delete state (this is
            // what was actually causing the delete button to look like it
            // did nothing). invalidate() still discards any open-time
            // refresh that's still in flight, so it can't overwrite this
            // with stale data once it resolves.
            invalidate();
            renderPanel(panel, overlay, refresh, invalidate);
        });
    });
}

function injectStyles() {
    if (document.getElementById('saved-routes-panel-styles')) return;
    const style = document.createElement('style');
    style.id = 'saved-routes-panel-styles';
    style.textContent = PANEL_STYLES;
    document.head.appendChild(style);
}

// Expose globally for the menu button
window.openSavedRoutesPanel = openSavedRoutesPanel;
